/**
 * Lazy-bind Vimeo/progressive file URLs onto a single background <video>.
 *
 * Markup uses data-video-src (and optional data-video-src-mobile) with no
 * <source> on the frontend, so only the visible breakpoint file is fetched.
 * Src is assigned only after the video is near the viewport and a matching
 * media query allows it. Skipped for reduced motion, Save-Data, and slow
 * connections.
 */

const DEFAULT_MOBILE_MQ = "(max-width: 767px)";
const NEAR_VIEWPORT_MARGIN = "600px 0px";
const initialized = new WeakSet();

function shouldSkipVideo() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return true;
  }

  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  if (!conn) {
    return false;
  }

  if (conn.saveData) {
    return true;
  }

  const type = String(conn.effectiveType || "").toLowerCase();
  return type === "slow-2g" || type === "2g";
}

function isMobileViewport(video) {
  const mq = video.dataset.videoMobileMq || DEFAULT_MOBILE_MQ;
  return window.matchMedia(mq).matches;
}

function allowsDesktopOnMobile(video) {
  return video.dataset.videoDesktopOnMobile !== "0";
}

function getActiveSrc(video) {
  const desktop = (video.dataset.videoSrc || "").trim();
  const mobile = (video.dataset.videoSrcMobile || "").trim();

  if (isMobileViewport(video)) {
    if (mobile) {
      return mobile;
    }
    return allowsDesktopOnMobile(video) ? desktop : "";
  }

  return desktop || mobile;
}

function unloadVideo(video) {
  video.pause();
  video.classList.remove("is-loaded");
  video.removeAttribute("src");
  video.querySelectorAll("source").forEach((el) => el.remove());
  video.load();
  delete video.dataset.videoBoundSrc;
}

function reveal(video) {
  video.classList.add("is-loaded");
}

function bindAndPlay(video, src) {
  if (!src) {
    return;
  }

  if (video.dataset.videoBoundSrc === src && video.readyState >= 2) {
    reveal(video);
    const resume = video.play();
    if (resume) {
      resume.catch(() => {});
    }
    return;
  }

  video.classList.remove("is-loaded");
  video.querySelectorAll("source").forEach((el) => el.remove());
  video.preload = "none";
  video.dataset.videoBoundSrc = src;
  video.src = src;
  video.load();

  const onReady = () => reveal(video);
  video.addEventListener("loadeddata", onReady, { once: true });
  video.addEventListener("canplay", onReady, { once: true });
  video.addEventListener("playing", onReady, { once: true });

  const playPromise = video.play();
  if (playPromise) {
    playPromise.catch(() => {
      console.log("[SOJ] Background video autoplay prevented");
    });
  }
}

function afterFirstPaint(callback) {
  const run = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(callback, { timeout: 800 });
      return;
    }
    window.setTimeout(callback, 0);
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run);
  });
}

function watchBreakpoint(video, apply) {
  const desktop = (video.dataset.videoSrc || "").trim();
  const mobile = (video.dataset.videoSrcMobile || "").trim();
  const needsBreakpointWatch =
    (mobile && mobile !== desktop) || !allowsDesktopOnMobile(video);
  if (!needsBreakpointWatch || video.dataset.videoMqBound === "1") {
    return;
  }

  video.dataset.videoMqBound = "1";

  const mqList = window.matchMedia(
    video.dataset.videoMobileMq || DEFAULT_MOBILE_MQ,
  );
  const onBreakpoint = () => apply();

  if (typeof mqList.addEventListener === "function") {
    mqList.addEventListener("change", onBreakpoint);
  } else if (typeof mqList.addListener === "function") {
    mqList.addListener(onBreakpoint);
  }
}

function startBinding(video) {
  if (shouldSkipVideo()) {
    video.removeAttribute("src");
    video.querySelectorAll("source").forEach((el) => el.remove());
    watchBreakpoint(video, () => {
      if (!shouldSkipVideo()) {
        applySrc(video);
      }
    });
    return;
  }

  applySrc(video);
  watchBreakpoint(video, () => applySrc(video));
}

function applySrc(video) {
  const src = getActiveSrc(video);
  if (src) {
    bindAndPlay(video, src);
    return;
  }
  unloadVideo(video);
}

function whenNearViewport(video, callback) {
  if (typeof IntersectionObserver === "undefined") {
    callback();
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        io.disconnect();
        callback();
      }
    },
    { root: null, rootMargin: NEAR_VIEWPORT_MARGIN, threshold: 0 },
  );

  io.observe(video);
}

function initBackgroundVideo(video) {
  if (!(video instanceof HTMLVideoElement) || initialized.has(video)) {
    return;
  }

  initialized.add(video);

  if (shouldSkipVideo()) {
    video.removeAttribute("src");
    video.querySelectorAll("source").forEach((el) => el.remove());
  }

  whenNearViewport(video, () => {
    afterFirstPaint(() => startBinding(video));
  });
}

function initBackgroundVideos(root = document) {
  root
    .querySelectorAll("video[data-video-src], video[data-video-src-mobile]")
    .forEach(initBackgroundVideo);
}

function init() {
  initBackgroundVideos();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

export { initBackgroundVideo, initBackgroundVideos };
